import { z } from "zod";
import {
  insertEmpresaSchema,
  insertUserProfileSchema,
  insertLaudoSchema,
  insertLaudoPecaSchema,
  insertLaudoPecaRespostaSchema,
  insertPecaSchema,
  insertLoteSchema,
  insertInviteCodeSchema,
  insertFaturaSchema,
  insertLancamentoSchema,
  insertVeiculoSchema,
  empresas,
  userProfiles,
  laudos,
  laudoPecas,
  pecas,
  lotes,
  inviteCodes,
  userInvites,
  perguntas,
  bancoPerguntasGlobais,
  faturas,
  lancamentos,
  veiculos,
  notificacoes,
  rtArts,
  apks,
  tutorialPdfs,
  tutorialTopicos,
  tutorialVideos,
} from "./schema";

export const errorSchemas = {
  validation: z.object({
    message: z.string(),
    field: z.string().optional(),
  }),
  notFound: z.object({
    message: z.string(),
  }),
  internal: z.object({
    message: z.string(),
  }),
};

export const api = {
  auth: {
    me: {
      method: "GET" as const,
      path: "/api/auth/me" as const,
      responses: {
        200: z.custom<
          typeof userProfiles.$inferSelect & {
            empresa: typeof empresas.$inferSelect;
          }
        >(),
        401: errorSchemas.notFound,
      },
    },
    login: {
      method: "POST" as const,
      path: "/api/auth/login" as const,
      input: z.object({
        email: z.string().email(),
        password: z.string().min(6),
      }),
      responses: {
        200: z.object({
          user: z.custom<typeof userProfiles.$inferSelect>(),
          session: z.any(),
        }),
        401: errorSchemas.notFound,
      },
    },
    loginDocumento: {
      method: "POST" as const,
      path: "/api/auth/login-documento" as const,
      input: z.object({
        documento: z.string().max(32),
        senha: z.string().max(256),
      }),
      responses: {
        200: z.object({
          access_token: z.string(),
          refresh_token: z.string(),
          token_type: z.string().optional(),
          expires_in: z.number().optional(),
          expires_at: z.number().optional(),
        }),
        401: errorSchemas.notFound,
        426: errorSchemas.notFound,
        429: errorSchemas.notFound,
        503: errorSchemas.notFound,
      },
    },
    esqueciSenha: {
      method: "POST" as const,
      path: "/api/auth/esqueci-senha" as const,
      input: z.object({
        email: z.string().email(),
      }),
      responses: {
        200: z.object({ message: z.string() }),
      },
    },
    register: {
      method: "POST" as const,
      path: "/api/auth/register" as const,
      input: z.object({
        email: z.string().email(),
        password: z.string().min(6),
        nome: z.string().min(2),
        codigoConvite: z.string(),
        cpfUsuario: z.string().optional(),
        whatsappUsuario: z.string().optional(),
        logradouroUsuario: z.string().optional(),
        numeroUsuario: z.string().optional(),
        complementoUsuario: z.string().optional(),
        bairroUsuario: z.string().optional(),
        cidadeUsuario: z.string().optional(),
        cepUsuario: z.string().optional(),
        estadoUsuario: z.string().optional(),
        empresa: z.object({
          razaoSocial: z.string(),
          nomeFantasia: z.string().optional(),
          cnpj: z.string().optional(),
          cpf: z.string().optional(),
          uf: z.string().length(2),
          email: z.string().email(),
          telefone: z.string().optional(),
          whatsapp: z.string().optional(),
          logradouro: z.string().optional(),
          numero: z.string().optional(),
          complemento: z.string().optional(),
          bairro: z.string().optional(),
          cidade: z.string().min(1),
          cep: z.string().optional(),
          tagEmpresa: z.string().optional(),
          tagParceiro: z.string().optional(),
          // ART para empresa parceira de negócios
          art: z.string().optional(),
          vencimentoArt: z.string().optional(), // ISO date string 'YYYY-MM-DD'
        }),
      }),
      responses: {
        201: z.object({
          user: z.custom<typeof userProfiles.$inferSelect>(),
          empresa: z.custom<Empresa>(),
        }),
        400: errorSchemas.validation,
      },
    },
    logout: {
      method: "POST" as const,
      path: "/api/auth/logout" as const,
      responses: {
        200: z.object({ message: z.string() }),
      },
    },
    registerUser: {
      method: "POST" as const,
      path: "/api/auth/register-user" as const,
      input: z.object({
        nome: z.string().min(2),
        email: z.string().email(),
        password: z.string().min(6),
        codigoConvite: z.string().min(1),
        crea: z.string().optional(),
        cpf: z.string().optional(),
      }),
      responses: {
        201: z.custom<typeof userProfiles.$inferSelect>(),
        400: errorSchemas.validation,
      },
    },
  },

  empresas: {
    tagParceiroDisponivel: {
      method: "GET" as const,
      path: "/api/empresas/tag-parceiro-disponivel" as const,
      responses: { 200: z.object({ disponivel: z.boolean(), tagNormalizada: z.string() }) },
    },
    list: {
      method: "GET" as const,
      path: "/api/empresas" as const,
      responses: {
        200: z.array(z.custom<Empresa>()),
      },
    },
    get: {
      method: "GET" as const,
      path: "/api/empresas/:id" as const,
      responses: {
        200: z.custom<Empresa>(),
        404: errorSchemas.notFound,
      },
    },
    update: {
      method: "PUT" as const,
      path: "/api/empresas/:id" as const,
      input: insertEmpresaSchema.partial(),
      responses: {
        200: z.custom<Empresa>(),
        404: errorSchemas.notFound,
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/empresas" as const,
      input: insertEmpresaSchema,
      responses: {
        201: z.custom<Empresa>(),
        400: errorSchemas.validation,
      },
    },
    parceiros: {
      method: "GET" as const,
      path: "/api/empresas/:id/parceiros" as const,
      responses: {
        200: z.array(z.custom<Empresa>()),
      },
    },
    createBatch: {
      method: "POST" as const,
      path: "/api/empresas/batch" as const,
      input: z.object({
        parceiros: z.array(
          z.object({
            razaoSocial: z.string().min(1),
            nomeFantasia: z.string().optional(),
            cnpj: z.string().optional(),
            cpf: z.string().optional(),
            email: z.string().email(),
            uf: z.string().length(2),
            cidade: z.string().min(1),
            telefone: z.string().optional(),
            idProprio: z.number().int().optional(),
            tagParceiro: z.string().optional(),
          })
        ).min(1),
      }),
      responses: {
        200: z.object({
          criados: z.number(),
          ignorados: z.number(),
          erros: z.array(z.object({ linha: z.number(), razao: z.string() })),
        }),
      },
    },
  },

  usuarios: {
    list: {
      method: "GET" as const,
      path: "/api/usuarios" as const,
      responses: {
        200: z.array(z.custom<typeof userProfiles.$inferSelect>()),
      },
    },
    get: {
      method: "GET" as const,
      path: "/api/usuarios/:id" as const,
      responses: {
        200: z.custom<typeof userProfiles.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/usuarios" as const,
      input: z.object({
        email: z.string().email("E-mail inválido"),
        password: z.string().min(6, "Senha deve ter ao menos 6 caracteres"),
        nome: z.string().min(2, "Nome deve ter ao menos 2 caracteres"),
        empresaId: z.string().uuid("Empresa inválida"),
        roles: z.array(z.string()).min(1, "Selecione ao menos um perfil"),
        telefone: z.string().optional(),
        whatsapp: z.string().optional(),
        cpf: z.string().optional(),
        crea: z.string().optional(),
        art: z.string().optional(),
        logradouro: z.string().optional(),
        numero: z.string().optional(),
        complemento: z.string().optional(),
        bairro: z.string().optional(),
        cidade: z.string().optional(),
        estado: z.string().length(2).optional(),
        cep: z.string().optional(),
      }),
      responses: {
        201: z.custom<typeof userProfiles.$inferSelect>(),
        400: errorSchemas.validation,
        403: errorSchemas.validation,
      },
    },
    update: {
      method: "PUT" as const,
      path: "/api/usuarios/:id" as const,
      input: insertUserProfileSchema.partial(),
      responses: {
        200: z.custom<typeof userProfiles.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    changePassword: {
      method: "POST" as const,
      path: "/api/usuarios/:id/change-password" as const,
      input: z
        .object({
          senhaAtual: z.string().min(6, "Senha atual obrigatória"),
          novaSenha: z
            .string()
            .min(6, "Nova senha deve ter ao menos 6 caracteres"),
          confirmarSenha: z.string(),
        })
        .refine((data) => data.novaSenha === data.confirmarSenha, {
          message: "Senhas não correspondem",
          path: ["confirmarSenha"],
        }),
      responses: {
        200: z.object({ message: z.string() }),
        400: errorSchemas.validation,
        401: errorSchemas.notFound,
        403: errorSchemas.validation,
      },
    },
    resetPassword: {
      method: "POST" as const,
      path: "/api/usuarios/:id/reset-password" as const,
      input: z
        .object({
          novaSenha: z
            .string()
            .min(6, "Nova senha deve ter ao menos 6 caracteres"),
          confirmarSenha: z.string(),
        })
        .refine((data) => data.novaSenha === data.confirmarSenha, {
          message: "Senhas não correspondem",
          path: ["confirmarSenha"],
        }),
      responses: {
        200: z.object({ message: z.string() }),
        400: errorSchemas.validation,
        401: errorSchemas.notFound,
        403: errorSchemas.validation,
      },
    },
  },

  laudos: {
    list: {
      method: "GET" as const,
      path: "/api/laudos" as const,
      responses: {
        200: z.array(z.custom<typeof laudos.$inferSelect>()),
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/laudos" as const,
      input: z.object({
        veiculoId: z.string().uuid(),
        parceiroId: z.string().uuid(),
        cartela: z.string().length(10).optional(),
        pecas: z.array(z.object({
          catalogoPecaId: z.string().uuid().optional(),
          sequencia: z.number().int(),
          etiqueta: z.string(),
          uf: z.string().length(2),
          nome: z.string(),
          tipoVeiculo: z.string(),
        })).optional(),
      }),
      responses: {
        201: z.custom<typeof laudos.$inferSelect>(),
        400: z.object({ message: z.string() }),
      },
    },
    get: {
      method: "GET" as const,
      path: "/api/laudos/:id" as const,
      responses: {
        200: z.custom<typeof laudos.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    update: {
      method: "PUT" as const,
      path: "/api/laudos/:id" as const,
      input: insertLaudoSchema.partial(),
      responses: {
        200: z.custom<typeof laudos.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    delete: {
      method: "DELETE" as const,
      path: "/api/laudos/:id" as const,
      responses: {
        200: z.object({ message: z.string() }),
        403: errorSchemas.notFound,
        404: errorSchemas.notFound,
      },
    },
    stats: {
      method: "GET" as const,
      path: "/api/laudos/stats" as const,
      responses: {
        200: z.object({
          total: z.number(),
          rascunho: z.number(),
          enviado_analise: z.number(),
          em_vistoria: z.number(),
          em_avaliacao_rt: z.number(),
          assinado: z.number(),
          finalizado: z.number(),
        }),
      },
    },
    statsPorParceiro: {
      method: "GET" as const,
      path: "/api/laudos/stats/por-parceiro" as const,
      responses: {
        200: z.array(
          z.object({
            parceiroId: z.string(),
            parceiroNome: z.string(),
            rascunho: z.number(),
            enviado_analise: z.number(),
            em_vistoria: z.number(),
            em_avaliacao_rt: z.number(),
            assinado: z.number(),
            finalizado: z.number(),
            faturados: z.number(),
            total: z.number(),
          }),
        ),
      },
    },
  },

  pecas: {
    list: {
      method: "GET" as const,
      path: "/api/pecas" as const,
      responses: {
        200: z.array(z.custom<typeof pecas.$inferSelect>()),
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/pecas" as const,
      input: insertPecaSchema,
      responses: {
        201: z.custom<typeof pecas.$inferSelect>(),
        400: errorSchemas.validation,
      },
    },
    update: {
      method: "PUT" as const,
      path: "/api/pecas/:id" as const,
      input: insertPecaSchema.partial(),
      responses: {
        200: z.custom<typeof pecas.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    createBatch: {
      method: "POST" as const,
      path: "/api/pecas/batch" as const,
      input: z.object({
        pecas: z
          .array(
            z.object({
              nome: z.string().min(1),
              tipoVeiculo: z.enum(["auto_leve", "moto", "caminhao", "onibus"]),
              ufs: z.array(z.string().length(2)).min(1),
              sequencia: z.number().int().positive().optional(),
            }),
          )
          .min(1),
      }),
      responses: {
        201: z.array(z.custom<typeof pecas.$inferSelect>()),
        400: errorSchemas.validation,
      },
    },
    delete: {
      method: "DELETE" as const,
      path: "/api/pecas/:id" as const,
      responses: {
        200: z.object({ message: z.string() }),
        403: errorSchemas.validation,
        404: errorSchemas.notFound,
      },
    },
    deleteBatch: {
      method: "DELETE" as const,
      path: "/api/pecas/tipo/:tipo" as const,
      responses: {
        200: z.object({ message: z.string() }),
        403: errorSchemas.validation,
      },
    },
  },

  pecaPerguntas: {
    list: {
      method: "GET" as const,
      path: "/api/peca-perguntas" as const,
      responses: {
        200: z.array(z.custom<typeof perguntas.$inferSelect>()),
      },
    },
    sync: {
      method: "POST" as const,
      path: "/api/peca-perguntas/sync" as const,
      input: z.object({
        nome: z.string().min(1),
        tipoVeiculo: z.enum(["auto_leve", "moto", "caminhao", "onibus"]),
        perguntas: z.array(z.string().min(1)).max(20),
      }),
      responses: {
        200: z.array(z.custom<typeof perguntas.$inferSelect>()),
        400: z.object({ message: z.string() }),
        403: z.object({ message: z.string() }),
      },
    },
  },

  perguntasPadrao: {
    list: {
      method: "GET" as const,
      path: "/api/perguntas-padrao" as const,
      responses: {
        200: z.array(z.custom<typeof perguntas.$inferSelect>()),
      },
    },
    sync: {
      method: "POST" as const,
      path: "/api/perguntas-padrao/sync" as const,
      input: z.object({
        tipoVeiculo: z.enum(["auto_leve", "moto", "caminhao", "onibus"]),
        perguntas: z.array(z.string().min(1)).max(20),
      }),
      responses: {
        200: z.array(z.custom<typeof perguntas.$inferSelect>()),
        400: z.object({ message: z.string() }),
        403: z.object({ message: z.string() }),
      },
    },
  },

  bancoPerguntas: {
    list: {
      method: "GET" as const,
      path: "/api/banco-perguntas" as const,
      responses: {
        200: z.array(z.custom<typeof bancoPerguntasGlobais.$inferSelect>()),
      },
    },
    sync: {
      method: "POST" as const,
      path: "/api/banco-perguntas/sync" as const,
      input: z.object({
        perguntas: z.array(z.string().min(1)).max(100),
      }),
      responses: {
        200: z.array(z.custom<typeof bancoPerguntasGlobais.$inferSelect>()),
        403: z.object({ message: z.string() }),
      },
    },
  },

  financeiro: {
    stats: {
      method: "GET" as const,
      path: "/api/financeiro/stats" as const,
      responses: {
        200: z.object({
          totalReceber: z.number(),
          totalRecebido: z.number(),
          totalVencido: z.number(),
          totalDespesas: z.number(),
        }),
      },
    },
    faturas: {
      list: {
        method: "GET" as const,
        path: "/api/financeiro/faturas" as const,
        responses: {
          200: z.array(z.custom<typeof faturas.$inferSelect>()),
        },
      },
      create: {
        method: "POST" as const,
        path: "/api/financeiro/faturas" as const,
        input: insertFaturaSchema,
        responses: {
          201: z.custom<typeof faturas.$inferSelect>(),
          400: z.object({ message: z.string() }),
          403: z.object({ message: z.string() }),
        },
      },
      update: {
        method: "PUT" as const,
        path: "/api/financeiro/faturas/:id" as const,
        input: insertFaturaSchema.partial(),
        responses: {
          200: z.custom<typeof faturas.$inferSelect>(),
          404: z.object({ message: z.string() }),
        },
      },
      delete: {
        method: "DELETE" as const,
        path: "/api/financeiro/faturas/:id" as const,
        responses: {
          200: z.object({ message: z.string() }),
        },
      },
    },
    lancamentos: {
      list: {
        method: "GET" as const,
        path: "/api/financeiro/lancamentos" as const,
        responses: {
          200: z.array(z.custom<typeof lancamentos.$inferSelect>()),
        },
      },
      create: {
        method: "POST" as const,
        path: "/api/financeiro/lancamentos" as const,
        input: insertLancamentoSchema,
        responses: {
          201: z.custom<typeof lancamentos.$inferSelect>(),
          400: z.object({ message: z.string() }),
        },
      },
      update: {
        method: "PUT" as const,
        path: "/api/financeiro/lancamentos/:id" as const,
        input: insertLancamentoSchema.partial(),
        responses: {
          200: z.custom<typeof lancamentos.$inferSelect>(),
        },
      },
      delete: {
        method: "DELETE" as const,
        path: "/api/financeiro/lancamentos/:id" as const,
        responses: {
          200: z.object({ message: z.string() }),
        },
      },
    },
  },

  veiculos: {
    list: {
      method: "GET" as const,
      path: "/api/veiculos" as const,
      responses: {
        200: z.array(z.custom<typeof veiculos.$inferSelect & { temLaudo: boolean }>()),
      },
    },
    get: {
      method: "GET" as const,
      path: "/api/veiculos/:id" as const,
      responses: {
        200: z.custom<typeof veiculos.$inferSelect & { temLaudo: boolean }>(),
        404: errorSchemas.notFound,
      },
    },
    parceiros: {
      method: "GET" as const,
      path: "/api/veiculos/parceiros/lista" as const,
      responses: {
        200: z.array(z.custom<Empresa>()),
        403: errorSchemas.validation,
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/veiculos" as const,
      input: insertVeiculoSchema,
      responses: {
        201: z.custom<typeof veiculos.$inferSelect>(),
        400: errorSchemas.validation,
      },
    },
    update: {
      method: "PUT" as const,
      path: "/api/veiculos/:id" as const,
      // criadoPor e ativo não são editáveis: exclusão é o DELETE.
      input: insertVeiculoSchema.omit({ criadoPor: true, ativo: true }).partial(),
      responses: {
        200: z.custom<typeof veiculos.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    delete: {
      method: "DELETE" as const,
      path: "/api/veiculos/:id" as const,
      responses: {
        200: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    createBatch: {
      method: "POST" as const,
      path: "/api/veiculos/batch" as const,
      input: z.object({
        veiculos: z.array(z.object({
          tipo: z.string(),
          placa: z.string(),
          numeroNota: z.string(),
          chassi: z.string(),
          cor: z.string(),
          marca: z.string(),
          modelo: z.string(),
          anoFabricacao: z.number().optional(),
          anoModelo: z.number().optional(),
          numeroMotor: z.string().optional(),
          renavam: z.string().optional(),
        })),
        parceiroId: z.string().uuid().optional(),
      }),
      responses: {
        200: z.object({
          criados: z.number(),
          ignorados: z.number(),
          erros: z.array(z.object({ linha: z.number(), razao: z.string() })),
        }),
      },
    },
  },

  laudoPecas: {
    update: {
      method: "PUT" as const,
      path: "/api/laudo-pecas/:id" as const,
      input: insertLaudoPecaSchema.pick({ destinacao: true }).partial().strict(),
      responses: {
        200: z.custom<typeof laudoPecas.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    updateRespostas: {
      method: "PUT" as const,
      path: "/api/laudo-pecas/:id/respostas" as const,
      input: z.object({
        respostas: z.array(
          z.object({ id: z.string().uuid(), resposta: z.boolean(), observacoes: z.string().max(2000).nullish() }),
        ),
      }),
      responses: {
        200: z.array(z.custom<typeof laudoPecas.$inferSelect>()),
        404: errorSchemas.notFound,
      },
    },
  },

  inviteCodes: {
    list: {
      method: "GET" as const,
      path: "/api/invite-codes" as const,
      responses: {
        200: z.array(z.custom<typeof inviteCodes.$inferSelect>()),
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/invite-codes" as const,
      input: z.object({
        tipo: z.enum(["VISTORIADORA", "PARCEIRO_DE_NEGOCIOS"]),
        maxUsos: z.number().int().positive().optional(),
      }),
      responses: {
        201: z.custom<typeof inviteCodes.$inferSelect>(),
        400: errorSchemas.validation,
      },
    },
    validate: {
      method: "POST" as const,
      path: "/api/invite-codes/validate" as const,
      input: z.object({ code: z.string() }),
      responses: {
        200: z.object({
          valid: z.boolean(),
          tipo: z.string().optional(),
          vistoriadoraId: z.string().optional(),
        }),
        404: errorSchemas.notFound,
      },
    },
    delete: {
      method: "DELETE" as const,
      path: "/api/invite-codes/:id" as const,
      responses: {
        200: z.object({ message: z.string() }),
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
      },
    },
  },

  userInvites: {
    list: {
      method: "GET" as const,
      path: "/api/user-invites" as const,
      responses: {
        200: z.array(z.custom<typeof userInvites.$inferSelect>()),
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/user-invites" as const,
      input: z.object({
        email: z.string().email().optional(),
        roles: z.array(z.string()).min(1).default(["tv"]),
        maxUsos: z.number().int().positive().optional(),
      }),
      responses: {
        201: z.custom<typeof userInvites.$inferSelect>(),
        400: errorSchemas.validation,
      },
    },
    validate: {
      method: "POST" as const,
      path: "/api/user-invites/validate" as const,
      input: z.object({ code: z.string().min(1) }),
      responses: {
        200: z.object({
          valid: z.boolean(),
          roles: z.array(z.string()).optional(),
          empresaId: z.string().optional(),
          email: z.string().optional(),
        }),
        404: errorSchemas.notFound,
      },
    },
    sendEmail: {
      method: "POST" as const,
      path: "/api/user-invites/:id/send-email" as const,
      input: z.object({ email: z.string().email() }),
      responses: {
        200: z.object({ message: z.string() }),
        400: errorSchemas.validation,
        501: errorSchemas.validation,
      },
    },
    delete: {
      method: "DELETE" as const,
      path: "/api/user-invites/:id" as const,
      responses: {
        200: z.object({ message: z.string() }),
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
      },
    },
  },
  configuracoes: {
    get: {
      method: "GET" as const,
      path: "/api/configuracoes" as const,
      responses: {
        200: z.any(),
      },
    },
    update: {
      method: "PUT" as const,
      path: "/api/configuracoes" as const,
      input: z.any(),
      responses: {
        200: z.any(),
      },
    },
  },

  precosVistoriadora: {
    list: {
      method: "GET" as const,
      path: "/api/precos-vistoriadora" as const,
      responses: { 200: z.array(z.any()) },
    },
    upsert: {
      method: "PUT" as const,
      path: "/api/precos-vistoriadora" as const,
      input: z.object({
        parceiroId: z.string().uuid().nullable().optional(),
        valorPorLaudo: z.string(),
      }),
      responses: { 200: z.any(), 400: z.object({ message: z.string() }) },
    },
    delete: {
      method: "DELETE" as const,
      path: "/api/precos-vistoriadora/:id" as const,
      responses: { 200: z.object({ message: z.string() }) },
    },
  },

  fichaTecnica: {
    list: {
      method: "GET" as const,
      path: "/api/ficha-tecnica" as const,
      responses: { 200: z.array(z.any()) },
    },
    create: {
      method: "POST" as const,
      path: "/api/ficha-tecnica" as const,
      input: z.object({
        userId: z.string().uuid(),
        especialidade: z.string().min(1),
        documentoFiscal: z.string().optional(),
      }),
      responses: { 201: z.any(), 400: z.object({ message: z.string() }) },
    },
    delete: {
      method: "DELETE" as const,
      path: "/api/ficha-tecnica/:id" as const,
      responses: { 200: z.object({ message: z.string() }) },
    },
  },

  lotes: {
    list: {
      method: "GET" as const,
      path: "/api/lotes" as const,
      responses: { 200: z.array(z.any()) },
    },
    get: {
      method: "GET" as const,
      path: "/api/lotes/:id" as const,
      responses: { 200: z.any(), 404: z.object({ message: z.string() }) },
    },
    fechar: {
      method: "POST" as const,
      path: "/api/lotes/:id/fechar" as const,
      input: z.object({ ordemServico: z.string().optional() }),
      responses: { 200: z.any(), 400: z.object({ message: z.string() }) },
    },
  },

  auditoria: {
    list: {
      method: "GET" as const,
      path: "/api/auditoria" as const,
      responses: {
        200: z.object({
          data: z.array(z.any()),
          total: z.number(),
          page: z.number(),
          limit: z.number(),
        }),
      },
    },
  },

  // Owner-only. As taxas seguem o formato da API do Asaas; a validação estrita fica em server/services/asaas/routes.ts.
  asaasTaxas: {
    obter: {
      method: "GET" as const,
      path: "/api/owner/asaas/taxas" as const,
      responses: { 200: z.any() },
    },
    sincronizar: {
      method: "POST" as const,
      path: "/api/owner/asaas/taxas/sincronizar" as const,
      input: z.object({ ambiente: z.enum(["producao", "sandbox"]).optional() }),
      responses: { 200: z.any(), 502: z.object({ message: z.string() }) },
    },
    fallback: {
      method: "PUT" as const,
      path: "/api/owner/asaas/taxas/fallback" as const,
      input: z.object({ ambiente: z.enum(["producao", "sandbox"]).optional(), taxas: z.any() }),
      responses: { 200: z.any(), 400: z.object({ message: z.string() }) },
    },
  },

  asaasSubconta: {
    obter: {
      method: "GET" as const,
      path: "/api/repasses/subconta" as const,
      responses: { 200: z.any() },
    },
    criar: {
      method: "POST" as const,
      path: "/api/repasses/subconta" as const,
      input: z.object({
        ambiente: z.enum(["producao", "sandbox", "simulado"]),
        aceitouTermos: z.boolean(),
        agendaRepasse: z.enum(["diario", "semanal", "mensal", "manual"]).optional(),
        vistoriadoraId: z.string().uuid().optional(),
      }).strict(),
      responses: { 200: z.any(), 400: z.object({ message: z.string() }) },
    },
    agendamento: {
      method: "PUT" as const,
      path: "/api/repasses/subconta/agendamento" as const,
      input: z.object({
        ambiente: z.enum(["producao", "sandbox", "simulado"]),
        agendaRepasse: z.enum(["diario", "semanal", "mensal", "manual"]),
        vistoriadoraId: z.string().uuid().optional(),
      }).strict(),
      responses: { 200: z.any(), 400: z.object({ message: z.string() }) },
    },
    cadastro: {
      method: "PUT" as const,
      path: "/api/repasses/subconta/cadastro" as const,
      input: z.object({
        vistoriadoraId: z.string().uuid().optional(),
        razaoSocial: z.string().trim().min(1),
        cnpj: z.string().min(14),
        email: z.string().email(),
        telefone: z.string().min(10),
        logradouro: z.string().trim().min(1),
        numero: z.string().trim().min(1),
        complemento: z.string().trim().nullable().optional(),
        bairro: z.string().trim().min(1),
        cidade: z.string().trim().min(1),
        uf: z.string().trim().length(2),
        cep: z.string().min(8),
        companyType: z.enum(["MEI", "LIMITED", "INDIVIDUAL", "ASSOCIATION"]),
        taxRegime: z.enum(["MEI", "NATIONAL_SIMPLE", "NORMAL_REGIME", "UNKNOWN"]),
        incomeValue: z.number().positive(),
      }).strict(),
      responses: { 200: z.any(), 400: z.object({ message: z.string() }) },
    },
  },

  asaasRepasses: {
    resumo: {
      method: "GET" as const,
      path: "/api/repasses/resumo" as const,
      responses: { 200: z.array(z.any()), 403: z.object({ message: z.string() }) },
    },
    ledger: {
      method: "GET" as const,
      path: "/api/repasses/ledger" as const,
      responses: { 200: z.array(z.any()), 403: z.object({ message: z.string() }) },
    },
    lotes: {
      method: "GET" as const,
      path: "/api/owner/repasses/lotes" as const,
      responses: { 200: z.array(z.any()), 403: z.object({ message: z.string() }) },
    },
    cutoverPreview: {
      method: "GET" as const,
      path: "/api/owner/asaas/cutover/preview" as const,
      responses: { 200: z.any(), 403: z.object({ message: z.string() }) },
    },
    fechar: {
      method: "POST" as const,
      path: "/api/repasses/fechar" as const,
      input: z.object({
        ambiente: z.enum(["producao", "sandbox", "simulado"]),
        vistoriadoraId: z.string().uuid().optional(),
      }).strict(),
      responses: { 200: z.any(), 400: z.object({ message: z.string() }), 403: z.object({ message: z.string() }) },
    },
    pixEmergencial: {
      method: "POST" as const,
      path: "/api/owner/repasses/:id/pix-emergencial" as const,
      input: z.object({ confirmar: z.literal(true) }).strict(),
      responses: { 200: z.any(), 400: z.object({ message: z.string() }), 403: z.object({ message: z.string() }) },
    },
    resolverIncerta: {
      method: "POST" as const,
      path: "/api/owner/repasses/:id/resolver-incerta" as const,
      input: z.discriminatedUnion("action", [
        z.object({ action: z.literal("adopt"), transferId: z.string().trim().min(1) }).strict(),
        z.object({ action: z.literal("confirm_absent") }).strict(),
      ]),
      responses: { 200: z.any(), 400: z.object({ message: z.string() }), 403: z.object({ message: z.string() }) },
    },
    externoRegistrar: {
      method: "POST" as const,
      path: "/api/owner/repasses/externo" as const,
      input: z.object({
        vistoriadoraId: z.string().uuid(),
        ambiente: z.enum(["producao", "sandbox", "simulado"]),
        modalidade: z.enum(["integral", "parcial"]),
        valorCents: z.number().int().positive().optional(),
        observacao: z.string().trim().max(500).optional(),
        chaveIdempotencia: z.string().uuid(),
        comprovanteId: z.string().uuid(),
      }).strict().superRefine((v, ctx) => {
        if (v.modalidade === "parcial" && v.valorCents === undefined) {
          ctx.addIssue({ code: "custom", path: ["valorCents"], message: "Informe o valor parcial." });
        }
        if (v.modalidade === "integral" && v.valorCents !== undefined) {
          ctx.addIssue({ code: "custom", path: ["valorCents"], message: "O integral usa o disponível; não envie valor." });
        }
      }),
      responses: { 200: z.any(), 400: z.object({ message: z.string() }), 403: z.object({ message: z.string() }) },
    },
    externoComprovanteEnviar: {
      method: "POST" as const,
      path: "/api/owner/repasses/externo/comprovantes" as const,
      input: z.object({
        vistoriadoraId: z.string().uuid(),
        ambiente: z.enum(["producao", "sandbox", "simulado"]),
        chaveIdempotencia: z.string().uuid(),
        nome: z.string().min(1).max(255),
      }).strict(),
      responses: { 200: z.object({ id: z.string().uuid(), nome: z.string() }), 400: z.object({ message: z.string() }), 403: z.object({ message: z.string() }) },
    },
    externoComprovanteLer: {
      method: "GET" as const,
      path: "/api/repasses/externo/:id/comprovante" as const,
      responses: { 403: z.object({ message: z.string() }), 404: z.object({ message: z.string() }) },
    },
    externoDesfazer: {
      method: "POST" as const,
      path: "/api/owner/repasses/externo/:id/desfazer" as const,
      input: z.object({ confirmar: z.literal(true) }).strict(),
      responses: { 200: z.any(), 400: z.object({ message: z.string() }), 403: z.object({ message: z.string() }) },
    },
    externoListar: {
      method: "GET" as const,
      path: "/api/owner/repasses/externo" as const,
      input: z.object({
        vistoriadoraId: z.string().uuid(),
        ambiente: z.enum(["producao", "sandbox", "simulado"]),
        limit: z.coerce.number().int().min(1).max(200).optional(),
      }).strict(),
      responses: { 200: z.any(), 400: z.object({ message: z.string() }), 403: z.object({ message: z.string() }) },
    },
  },

  asaasCotacoes: {
    metodos: {
      method: "GET" as const,
      path: "/api/asaas/metodos-pagamento" as const,
      responses: { 200: z.object({ methods: z.array(z.enum(["pix", "boleto", "cartao_credito", "cartao_debito"])) }) },
    },
    creditos: {
      method: "POST" as const,
      path: "/api/asaas/cotacoes/creditos" as const,
      input: z.object({
        quantities: z.record(z.string(), z.number().int().min(0).max(999)),
        method: z.enum(["pix", "boleto", "cartao_credito", "cartao_debito"]),
        installments: z.number().int().min(1).max(21).optional(),
      }).strict(),
      responses: { 200: z.any(), 400: z.object({ message: z.string() }) },
    },
    fatura: {
      method: "POST" as const,
      path: "/api/asaas/cotacoes/faturas/:tipo/:id" as const,
      input: z.object({
        method: z.enum(["pix", "boleto", "cartao_credito", "cartao_debito"]),
        installments: z.number().int().min(1).max(21).optional(),
      }).strict(),
      responses: { 200: z.any(), 400: z.object({ message: z.string() }) },
    },
    confirmar: {
      method: "POST" as const,
      path: "/api/asaas/cotacoes/:id/confirmar" as const,
      input: z.object({}).strict(),
      responses: { 200: z.any(), 409: z.object({ message: z.string() }) },
    },
  },

  vendas: {
    historicoOwner: {
      method: "GET" as const,
      path: "/api/vendas/historico/owner" as const,
      responses: {
        200: z.array(z.any()),
      },
    },
  },

  notificacoes: {
    list: {
      method: "GET" as const,
      path: "/api/notificacoes" as const,
      responses: {
        200: z.array(z.custom<typeof notificacoes.$inferSelect>()),
      },
    },
    marcarLida: {
      method: "PATCH" as const,
      path: "/api/notificacoes/:id/lida" as const,
      responses: {
        200: z.custom<typeof notificacoes.$inferSelect>(),
        404: z.object({ message: z.string() }),
      },
    },
    marcarTodasLidas: {
      method: "POST" as const,
      path: "/api/notificacoes/ler-todas" as const,
      responses: {
        200: z.object({ message: z.string() }),
      },
    },
  },

  rtArts: {
    list: {
      method: "GET" as const,
      path: "/api/usuarios/:rtId/arts" as const,
      responses: {
        200: z.array(z.custom<typeof rtArts.$inferSelect>()),
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/usuarios/arts" as const,
      input: z.object({
        rtId: z.string().uuid(),
        numeroArt: z.string().trim().min(1),
        descricao: z.string().optional(),
        dataInicio: z.string().date(),
        dataFim: z.string().date().optional().nullable(),
        status: z.enum(["ativa", "encerrada"]).default("ativa"),
      }),
      responses: {
        201: z.custom<typeof rtArts.$inferSelect>(),
      },
    },
    update: {
      method: "PUT" as const,
      path: "/api/usuarios/arts/:id" as const,
      input: z.object({
        status: z.enum(["ativa", "encerrada"]).optional(),
        dataFim: z.string().date().optional().nullable(),
        numeroArt: z.string().trim().min(1).optional(),
        descricao: z.string().optional().nullable(),
        dataInicio: z.string().date().optional(),
      }),
      responses: {
        200: z.custom<typeof rtArts.$inferSelect>(),
      },
    },
  },
  apks: {
    list: {
      method: "GET" as const,
      path: "/api/owner/apks" as const,
      responses: {
        200: z.array(z.custom<typeof apks.$inferSelect>()),
      },
    },
    upload: {
      method: "POST" as const,
      path: "/api/owner/apks" as const,
      responses: {
        201: z.custom<typeof apks.$inferSelect>(),
        400: errorSchemas.validation,
      },
    },
    setPrincipal: {
      method: "PUT" as const,
      path: "/api/owner/apks/:id/principal" as const,
      responses: {
        200: z.custom<typeof apks.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    delete: {
      method: "DELETE" as const,
      path: "/api/owner/apks/:id" as const,
      responses: {
        200: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    download: {
      method: "GET" as const,
      path: "/api/apks/download" as const,
      responses: {
        200: z.object({ url: z.string(), versao: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    versaoAtual: {
      method: "GET" as const,
      path: "/api/apks/versao-atual" as const,
      responses: {
        200: z.object({
          versao: z.string(),
          versaoMinima: z.string().nullable(),
        }),
        404: errorSchemas.notFound,
      },
    },
  },
  tutoriaisPdfs: {
    list: {
      method: "GET" as const,
      path: "/api/owner/tutoriais/pdfs" as const,
      responses: {
        200: z.array(z.custom<typeof tutorialPdfs.$inferSelect>()),
      },
    },
    upload: {
      method: "POST" as const,
      path: "/api/owner/tutoriais/pdfs" as const,
      responses: {
        201: z.custom<typeof tutorialPdfs.$inferSelect>(),
        400: errorSchemas.validation,
      },
    },
    setPrincipal: {
      method: "PUT" as const,
      path: "/api/owner/tutoriais/pdfs/:id/principal" as const,
      responses: {
        200: z.custom<typeof tutorialPdfs.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    delete: {
      method: "DELETE" as const,
      path: "/api/owner/tutoriais/pdfs/:id" as const,
      responses: {
        200: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
  },
  tutoriaisTopicos: {
    list: {
      method: "GET" as const,
      path: "/api/owner/tutoriais/topicos" as const,
      responses: {
        200: z.array(z.custom<typeof tutorialTopicos.$inferSelect>()),
      },
    },
    create: {
      method: "POST" as const,
      path: "/api/owner/tutoriais/topicos" as const,
      input: z.object({ papel: z.string().min(1), titulo: z.string().min(1) }),
      responses: {
        201: z.custom<typeof tutorialTopicos.$inferSelect>(),
        400: errorSchemas.validation,
      },
    },
    delete: {
      method: "DELETE" as const,
      path: "/api/owner/tutoriais/topicos/:id" as const,
      responses: {
        200: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    listVideos: {
      method: "GET" as const,
      path: "/api/owner/tutoriais/topicos/:id/videos" as const,
      responses: {
        200: z.array(z.custom<typeof tutorialVideos.$inferSelect>()),
        404: errorSchemas.notFound,
      },
    },
    uploadVideo: {
      method: "POST" as const,
      path: "/api/owner/tutoriais/topicos/:id/videos" as const,
      responses: {
        201: z.custom<typeof tutorialVideos.$inferSelect>(),
        400: errorSchemas.validation,
        404: errorSchemas.notFound,
      },
    },
    setVideoPrincipal: {
      method: "PUT" as const,
      path: "/api/owner/tutoriais/videos/:id/principal" as const,
      responses: {
        200: z.custom<typeof tutorialVideos.$inferSelect>(),
        404: errorSchemas.notFound,
      },
    },
    deleteVideo: {
      method: "DELETE" as const,
      path: "/api/owner/tutoriais/videos/:id" as const,
      responses: {
        200: z.object({ message: z.string() }),
        404: errorSchemas.notFound,
      },
    },
  },
  tutoriais: {
    list: {
      method: "GET" as const,
      path: "/api/tutoriais" as const,
      responses: {
        200: z.array(
          z.object({
            papel: z.string(),
            pdf: z.custom<typeof tutorialPdfs.$inferSelect>().nullable(),
            topicos: z.array(
              z.object({
                id: z.string(),
                titulo: z.string(),
                video: z.custom<typeof tutorialVideos.$inferSelect>().nullable(),
              }),
            ),
          }),
        ),
      },
    },
    downloadPdf: {
      method: "GET" as const,
      path: "/api/tutoriais/pdfs/:id/download" as const,
      responses: {
        200: z.object({ url: z.string() }),
        404: errorSchemas.notFound,
      },
    },
    downloadVideo: {
      method: "GET" as const,
      path: "/api/tutoriais/videos/:id/download" as const,
      responses: {
        200: z.object({ url: z.string() }),
        404: errorSchemas.notFound,
      },
    },
  },
};

export function buildUrl(
  path: string,
  params?: Record<string, string | number>,
): string {
  let url = path;
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (url.includes(`:${key}`)) {
        url = url.replace(`:${key}`, String(value));
      }
    });
  }
  return url;
}
import type { Empresa } from "./schema";
